import {MonitorEvaluationPayload} from "../../pages/alerts/MetricAlert";
import {AxiosPromise} from "axios";
import axios from "../../utility/customAxios";
import {plainToInstance} from "class-transformer";
import {MetricFunction} from "../../components/Dashboarding/widgets/MetricSelector";
import {Transform} from "../../components/Dashboarding/widgets/TransformSelector";
import React from "react";
import {RuntimeVariable} from "../../components/Dashboarding/Dashboard";
import {substituteVariablesInFilters, substituteVariablesInMetoroQlQuery} from "../../components/Charts/utils";

export interface GetKubernetesMetricsRequest {
    metricName: string;
    startTime: number;
    endTime: number;
    filters: Map<string, string[]>;
    excludeFilters?: Map<string, string[]>;
    splits: string[];
    aggregation: string;
    isRate?: boolean;
    functions: MetricFunction[];
    limitResults: boolean;
    bucketSize?: number;
    jsonPath?: string;
}


export interface GetMetricRequest {
    metricName: string;
    startTime: number;
    endTime: number;
    filters: Map<string, string[]>;
    excludeFilters?: Map<string, string[]>;
    splits: string[];
    aggregation: string;
    isRate?: boolean;
    functions: MetricFunction[];
    limitResults: boolean;
    bucketSize?: number;
}

interface GetMetricAggregateEvaluationRequest extends GetMetricRequest {
    type: string;
    aggregateParams: MonitorEvaluationPayload;
}

export class GetMetricResponse {
    constructor(metric: Metric, isResultLimited: boolean, resultLen: number) {
        this.metric = metric;
        this.isResultLimited = isResultLimited;
        this.resultLen = resultLen;
    }

    metric: Metric;
    isResultLimited: boolean;
    resultLen: number;
}

interface MetricVisualization {
    displayName?: string
    lineDash?: number[]
    lineDotColor?: string,
    lineColor?: string,
    lineDotSize?: number
    customCategoryColors?: Map<Record<string, string>, string[]>
    // Optional unit override - if specified, this unit will be used instead of the backend-provided unit
    unit?: string
}

export interface Metric {
    // The name of the metric
    name: string;
    // The time series that make up this metric
    timeSeries: TimeSeries[];
    // The type of the metric, e.g. "gauge", "counter", etc.
    type: string;
    // The unit of the metric in UCUM format
    unit: string;
    // The attributes that are common to all time series
    attributes: Map<string, string[]>;
    metricVisualization?: MetricVisualization
}

export interface TimeSeries {
    data: DataPoint[];
    // The attributes that are specific to this time series
    attributes: Map<string, string>;
}

interface DataPoint {
    // The time at which the data point was recorded in milliseconds since epoch
    time: number;
    // The value of the data point
    value: number;
}

export enum MetricType {
    Metric = "metric",
    Trace = "trace",
    Kubernetes = "kubernetes_resource",
    Logs = "logs"
}

export interface MetoroQLQueryRequest {
    query: string;
    bucketSize?: number;
    startTime: number;
    endTime: number;
}

export interface SingleMetricRequest {
    type: MetricType;
    metric?: GetMetricRequest;
    trace?: GetTraceMetricRequest;
    logs?: GetLogMetricsRequest;
    kubernetes?: GetKubernetesMetricsRequest;
    // If this is true then we won't return the result of this metric request, this is pretty much only useful when using a formula
    shouldNotReturn?: boolean;
    // FormulaIdentifier is a unique identifier for the result of this metric request.
    // For example, if this value is "a" then formulas can refer to the result of this request as "a"
    formulaIdentifier: string;
}

export interface GetMultiMetricAggregateEvaluationRequest extends MultiMetricRequest {
    aggregateParams: MonitorEvaluationPayload;
}

export interface AggregateParams {
    evaluationFunction: string,
    evaluationSplits?: string[]
}

export interface MultiMetricRequest {
    metrics: SingleMetricRequest[];
    formulas?: Formula[];
    metoroQLQueries?: MetoroQLQuerySpecifier[];
    environments?: string[];
}

export interface SingleMetricResponse {
    type: MetricType;
    metric?: Metric;
    isResultLimited?: boolean;
    formulaIdentifier: string;
    resultLen?: number;
    error?: string;
}

export interface MultiMetricResponse {
    metrics: SingleMetricResponse[];
}

interface GetAllTraceMetricsRequest {
    startTime: number;
    endTime: number;
    serviceNames?: string[];
    filters?: any;
    excludeFilters?: any;
    splits?: string[];
    regexes?: string[];
    excludeRegexes?: string[];
    onlyNumRequests?: boolean;
    environments?: string[];
}

export interface GetTraceMetricRequest {
    startTime: number;
    endTime: number;
    serviceNames?: string[];
    filters?: any;
    excludeFilters?: any;
    splits?: string[];
    regexes?: string[];
    excludeRegexes?: string[];
    environments?: string[];
    aggregate: string;
    functions: MetricFunction[];
    limitResults: boolean;
    bucketSize?: number;
}

class GetAllTraceMetricsResponse {
    constructor(metrics: Metric[]) {
        this.metrics = metrics;
    }

    metrics: Metric[];
}

export interface GetLogMetricsRequest {
    startTime: number;
    endTime: number;
    filters?: Map<string, string[]>;
    excludeFilters?: Map<string, string[]>;
    splits?: string[];
    regexes?: string[];
    excludeRegexes?: string[];
    environments?: string[];
    functions: MetricFunction[];
    transforms: Transform[];
    limitResults: boolean;
    bucketSize?: number;
    abortController?: AbortController;
}

class GetLogMetricsResponse {
    constructor(metric: Metric) {
        this.metric = metric;
    }

    metric: Metric;
}

async function GetLogMetrics(request: GetLogMetricsRequest, abortController: AbortController): Promise<GetLogMetricsResponse> {
    const transformed = {
        ...request,
        filters: request.filters ? Object.fromEntries(request.filters) : undefined,
        excludeFilters: request.excludeFilters ? Object.fromEntries(request.excludeFilters) : undefined,
    }
    const d: AxiosPromise<GetMetricResponse> = axios.post("/api/v1/logMetrics", transformed, {
            signal: abortController.signal
        }
    )
    let awaited = (await d).data;
    return plainToInstance(GetLogMetricsResponse, awaited);
}

async function GetAllTraceMetrics(request: GetAllTraceMetricsRequest, abortController: AbortController): Promise<GetAllTraceMetricsResponse> {
    const transformed = {
        ...request,
        filters: request.filters ? Object.fromEntries(request.filters) : undefined,
        excludeFilters: request.excludeFilters ? Object.fromEntries(request.excludeFilters) : undefined,
    }
    const d: AxiosPromise<GetMetricResponse> = axios.post("/api/v1/traceMetrics", transformed, {
            signal: abortController.signal
        }
    )
    let awaited = (await d).data;
    return plainToInstance(GetAllTraceMetricsResponse, awaited);
}

async function GetTraceMetric(request: GetTraceMetricRequest): Promise<GetMetricResponse> {
    const transformed = {
        ...request,
        filters: request.filters ? Object.fromEntries(request.filters) : undefined,
        excludeFilters: request.excludeFilters ? Object.fromEntries(request.excludeFilters) : undefined,
    }
    const d: AxiosPromise<GetMetricResponse> = axios.post("/api/v1/traceMetric", transformed,
    )
    let awaited = (await d).data;
    return plainToInstance(GetMetricResponse, awaited);
}

async function GetKubernetesMetric(request: GetKubernetesMetricsRequest,
                                   abortController: AbortController,
                                   setAbortController: React.Dispatch<React.SetStateAction<AbortController>>
): Promise<GetMetricResponse> {
    const transformed = {
        ...request,
        filters: Object.fromEntries(request.filters),
        excludeFilters: request.excludeFilters ? Object.fromEntries(request.excludeFilters) : undefined,
    }
    abortController.abort()
    let newAbortController = new AbortController();
    setAbortController(newAbortController)
    const d: AxiosPromise<GetMetricResponse> = axios.post("/api/v1/metric", transformed, {
        signal: newAbortController.signal
    })
    d.catch((e) => {
        if (e.name === "AbortError") {
            console.log("Aborted")
        }
    })
    let awaited = (await d).data;
    return plainToInstance(GetMetricResponse, awaited);
}


async function GetMetric(request: GetMetricRequest,
                         abortController: AbortController,
                         setAbortController: React.Dispatch<React.SetStateAction<AbortController>>
): Promise<GetMetricResponse> {
    const transformed = {
        ...request,
        filters: Object.fromEntries(request.filters),
        excludeFilters: request.excludeFilters ? Object.fromEntries(request.excludeFilters) : undefined,
    }
    abortController.abort()
    let newAbortController = new AbortController();
    setAbortController(newAbortController)
    const d: AxiosPromise<GetMetricResponse> = axios.post("/api/v1/metric", transformed, {
        signal: newAbortController.signal
    })
    d.catch((e) => {
        if (e.name === "AbortError") {
            console.log("Aborted")
        }
    })
    let awaited = (await d).data;
    return plainToInstance(GetMetricResponse, awaited);
}


export async function GetAggregateMultiMetricEvaluationNew(request: MultiMetricRequest, aggregateParams: AggregateParams): Promise<GetMetricResponse> {
    const processedRequest = {
        metrics: request.metrics.map(req => ({
            ...req,
            kubernetes: req.kubernetes && {
                ...req.kubernetes,
                filters: mapToObject(req.kubernetes.filters),
                excludeFilters: mapToObject(req.kubernetes.excludeFilters)
            },
            metric: req.metric && {
                ...req.metric,
                filters: mapToObject(req.metric.filters),
                excludeFilters: mapToObject(req.metric.excludeFilters)
            },
            logs: req.logs && {
                ...req.logs,
                filters: mapToObject(req.logs.filters),
                excludeFilters: mapToObject(req.logs.excludeFilters)
            },
            trace: req.trace && {
                ...req.trace,
                filters: mapToObject(req.trace.filters),
                excludeFilters: mapToObject(req.trace.excludeFilters)
            }
        })),
        metoroQLQueries: request.metoroQLQueries,
        formulas: request.formulas,
        aggregateParams: aggregateParams
    };


    const d: AxiosPromise<GetMetricResponse> = axios.post("/api/v1/metrics/aggregate", processedRequest)
    let awaited = (await d).data;
    return plainToInstance(GetMetricResponse, awaited);
}


async function GetAggregateMultiMetricEvaluation(
    request: MultiMetricRequest,
    aggregateParams: MonitorEvaluationPayload,
    abortController?: AbortController,
    setAbortController?: React.Dispatch<React.SetStateAction<AbortController>>
): Promise<GetMetricResponse> {
    const processedRequest = {
        metrics: request.metrics.map(req => ({
            ...req,
            kubernetes: req.kubernetes && {
                ...req.kubernetes,
                filters: mapToObject(req.kubernetes.filters),
                excludeFilters: mapToObject(req.kubernetes.excludeFilters)
            },
            metric: req.metric && {
                ...req.metric,
                filters: mapToObject(req.metric.filters),
                excludeFilters: mapToObject(req.metric.excludeFilters)
            },
            logs: req.logs && {
                ...req.logs,
                filters: mapToObject(req.logs.filters),
                excludeFilters: mapToObject(req.logs.excludeFilters)
            },
            trace: req.trace && {
                ...req.trace,
                filters: mapToObject(req.trace.filters),
                excludeFilters: mapToObject(req.trace.excludeFilters)
            }
        })),
        formulas: request.formulas,
        metoroQLQueries: request.metoroQLQueries,
        environments: request.environments || [],
        aggregateParams: aggregateParams
    };
    abortController?.abort();
    let newAbortController = new AbortController();
    setAbortController && setAbortController(newAbortController);

    const d: AxiosPromise<GetMetricResponse> = axios.post("/api/v1/metrics/aggregate", processedRequest, {
       signal: newAbortController.signal
    })
    d.catch((e) => {
        if (e.name === "AbortError") {
            console.log("Aborted")
        }
    })
    let awaited = (await d).data;
    return plainToInstance(GetMetricResponse, awaited);
}


async function GetAggregateMetricEvaluation(request: GetMetricAggregateEvaluationRequest): Promise<GetMetricResponse> {
    const transformed = {
        ...request,
        filters: Object.fromEntries(request.filters),
        excludeFilters: request.excludeFilters ? Object.fromEntries(request.excludeFilters) : undefined,
    }

    const d: AxiosPromise<GetMetricResponse> = axios.post("/api/v1/metric/aggregate", transformed)
    let awaited = (await d).data;
    return plainToInstance(GetMetricResponse, awaited);
}

function mapToObject<T>(map: Map<string, T> | undefined): Record<string, T> {
    if (!map) return {};
    return Object.fromEntries(map.entries());
}

async function GetMultiMetrics(request: MultiMetricRequest,
                               abortController: AbortController,
                               setAbortController: React.Dispatch<React.SetStateAction<AbortController>>
): Promise<MultiMetricResponse> {
    const processedRequest = {
        metrics: request.metrics.map(req => ({
            ...req,
            kubernetes: req.kubernetes && {
                ...req.kubernetes,
                filters: mapToObject(req.kubernetes.filters),
                excludeFilters: mapToObject(req.kubernetes.excludeFilters)
            },
            metric: req.metric && {
                ...req.metric,
                filters: mapToObject(req.metric.filters),
                excludeFilters: mapToObject(req.metric.excludeFilters)
            },
            logs: req.logs && {
                ...req.logs,
                filters: mapToObject(req.logs.filters),
                excludeFilters: mapToObject(req.logs.excludeFilters)
            },
            trace: req.trace && {
                ...req.trace,
                filters: mapToObject(req.trace.filters),
                excludeFilters: mapToObject(req.trace.excludeFilters)
            }
        })),
        formulas: request.formulas,
        metoroQLQueries: request.metoroQLQueries,
        environments: request.environments || []
    };

    abortController.abort();
    let newAbortController = new AbortController();
    setAbortController(newAbortController);

    try {
        const response = await axios.post("/api/v1/metrics", processedRequest, {
            signal: newAbortController.signal
        });
        return response.data;
    } catch (error: any) {
        // Detect all types of cancellation errors and handle them quietly
        if (error.name === "AbortError" ||
            error.code === "ECONNABORTED" ||
            error.code === 20 || // Chrome's ERR_ABORTED
            (error.message && (
                error.message.includes("canceled") ||
                error.message.includes("cancelled") ||
                error.message.includes("abort")
            ))) {
            console.log("Request was aborted or cancelled");
            throw error;
        }

        // Log error for debugging
        console.error("Error in GetMultiMetrics:", error);

        // Preserve the original error structure to make it accessible in the UI
        throw error;
    }
}

export interface Formula {
    formula: string;
    visualization?: MetricVisualization;
}

export {GetAggregateMultiMetricEvaluation};
export {GetAggregateMetricEvaluation};
export {GetMetric};
export {GetTraceMetric};
export {GetKubernetesMetric};
export {GetAllTraceMetrics};
export {GetLogMetrics};
export {GetLogMetricsResponse};
export {GetAllTraceMetricsResponse};
export {GetMultiMetrics};
export type {MetricVisualization};

export interface MetoroQLQuerySpecifier {
    query: string;
    bucketSize?: number;
    visualization?: MetricVisualization;
    shouldNotReturn?: boolean;
}

export interface MetricSpecifier {
    metricName: string;
    filters?: Map<string, string[]>;
    excludeFilters?: Map<string, string[]>;
    splits?: string[];
    aggregation: string;
    bucketSize?: number;
    functions: MetricFunction[];
    transforms?: Transform[];
    metricType: MetricType;
    visualization?: MetricVisualization;
    jsonPath?: string;
    regexes?: string[],
    excludeRegexes?: string[],
    shouldNotReturn?: boolean;
}

// Interface for MetoroQL to MetricSpecifier conversion response
export interface MetoroQLToMetricSpecifierResponse {
    metricSpecifiers: MetricSpecifier[];
    formulas: Formula[];
}

// Interface for MetricSpecifier to MetoroQL conversion response
export interface MetricSpecifierToMetoroQLResponse {
    queries: string[];
}

/**
 * Converts MetoroQL queries to metric specifiers
 * @param queries Array of MetoroQL query strings
 * @returns Promise that resolves to MetricSpecifier and Formula arrays
 */
export async function convertMetoroQLToMetricSpecifier(queries: string[]): Promise<MetoroQLToMetricSpecifierResponse> {
    try {
        const response = await axios.post<MetoroQLToMetricSpecifierResponse>("/api/v1/metoroql/convert/metoroqlToMetricSpecifier", {
            queries: queries
        });
        // We need to make sure we convert all of the objects to maps where appropriate
        for (let x = 0; x < response.data.metricSpecifiers.length; x++) {
            if (!(response.data.metricSpecifiers[x].filters instanceof Map)) {
                // @ts-ignore
                response.data.metricSpecifiers[x].filters = new Map(Object.entries(response.data.metricSpecifiers[x].filters))
            }
            if (!(response.data.metricSpecifiers[x].excludeFilters instanceof Map)) {
                // @ts-ignore
                response.data.metricSpecifiers[x].excludeFilters = new Map(Object.entries(response.data.metricSpecifiers[x].excludeFilters))
            }
        }

        return response.data;
    } catch (error) {
        console.error("Error converting MetoroQL to metric specifiers:", error);
        throw error;
    }
}

/**
 * Converts metric specifiers to MetoroQL queries
 * @param metricSpecifiers Array of MetricSpecifier objects
 * @param formulas Optional array of Formula objects
 * @returns Promise that resolves to an array of MetoroQL query strings
 */
export async function convertMetricSpecifierToMetoroQL(
    metricSpecifiers: MetricSpecifier[],
    formulas?: Formula[]
): Promise<MetricSpecifierToMetoroQLResponse> {
    try {
        for (let x = 0; x < metricSpecifiers.length; x++) {
            if (metricSpecifiers[x].filters instanceof Map) {
                // @ts-ignore
                metricSpecifiers[x].filters = Object.fromEntries(metricSpecifiers[x].filters)
            }
            if (metricSpecifiers[x].excludeFilters instanceof Map) {
                // @ts-ignore
                metricSpecifiers[x].excludeFilters = Object.fromEntries(metricSpecifiers[x].excludeFilters)
            }
        }

        const response = await axios.post<MetricSpecifierToMetoroQLResponse>("/api/v1/metoroql/convert/metricSpecifierToMetoroql", {
            metricSpecifiers: metricSpecifiers,
            formulas: formulas || []
        });

        return response.data;
    } catch (error) {
        console.error("Error converting metric specifiers to MetoroQL:", error);
        throw error;
    }
}

export function createMultiMetricRequest(
    metricSpecifiers: MetricSpecifier[],
    startTime: number,
    endTime: number,
    showAll: boolean,
    formulas?: Formula[],
    variables: RuntimeVariable[] = [],
    metoroQLQueries?: MetoroQLQuerySpecifier[],
    environments?: string[]
): MultiMetricRequest {
    let singleMetricRequests: SingleMetricRequest[] = [];
    for (let i = 0; i < metricSpecifiers.length; i++) {
        const metric = metricSpecifiers[i];
        let filters = metric.filters;
        if (filters === undefined) {
            filters = new Map<string, string[]>();
        }
        // Substitute the variables in the filters
        filters = substituteVariablesInFilters(filters, variables);

        let splits = metric.splits;
        if (splits === undefined) {
            splits = [];
        }

        // Create the formula identifier (a, b, c, etc.)
        const formulaIdentifier = String.fromCharCode(97 + i);

        if (metric.metricType === MetricType.Trace) {
            const request: GetTraceMetricRequest = {
                startTime: startTime,
                endTime: endTime,
                regexes: metric.regexes,
                excludeRegexes: metric.excludeRegexes,
                filters: filters,
                excludeFilters: metric.excludeFilters,
                splits: metric.splits,
                aggregate: metric.aggregation,
                functions: metric.functions,
                limitResults: !showAll,
                bucketSize: metric.bucketSize
            };
            singleMetricRequests.push({
                type: MetricType.Trace,
                trace: request,
                formulaIdentifier,
                shouldNotReturn: metric.shouldNotReturn
            });
        } else if (metric.metricType === MetricType.Logs) {
            const request: GetLogMetricsRequest = {
                startTime: startTime,
                endTime: endTime,
                filters: filters,
                excludeFilters: metric.excludeFilters,
                splits: splits,
                regexes: metric.regexes,
                excludeRegexes: metric.excludeRegexes,
                functions: metric.functions,
                transforms: metric.transforms || [],
                limitResults: !showAll,
                bucketSize: metric.bucketSize
            }
            singleMetricRequests.push({
                type: MetricType.Logs,
                logs: request,
                formulaIdentifier,
                shouldNotReturn: metric.shouldNotReturn
            })
        } else if (metric.metricType === MetricType.Kubernetes) {
            const request: GetKubernetesMetricsRequest = {
                metricName: metric.metricName,
                startTime: startTime,
                endTime: endTime,
                filters: filters,
                excludeFilters: metric.excludeFilters,
                splits: splits,
                aggregation: metric.aggregation,
                functions: metric.functions,
                limitResults: !showAll,
                bucketSize: metric.bucketSize,
                jsonPath: metric.jsonPath
            };
            singleMetricRequests.push({
                type: MetricType.Kubernetes,
                kubernetes: request,
                formulaIdentifier,
                shouldNotReturn: metric.shouldNotReturn
            })
        } else {
            const request: GetMetricRequest = {
                metricName: metric.metricName,
                startTime: startTime,
                endTime: endTime,
                filters: filters,
                excludeFilters: metric.excludeFilters,
                splits: splits,
                aggregation: metric.aggregation,
                functions: metric.functions,
                limitResults: !showAll,
                bucketSize: metric.bucketSize
            };
            singleMetricRequests.push({
                type: MetricType.Metric,
                metric: request,
                formulaIdentifier,
                shouldNotReturn: metric.shouldNotReturn
            })
        }
    }

    let qlQueries: MetoroQLQueryRequest[] | undefined = undefined;
    if (metoroQLQueries) {
        qlQueries = metoroQLQueries.map(query => ({
            query: substituteVariablesInMetoroQlQuery(query.query, variables),
            bucketSize: query.bucketSize,
            startTime: startTime,
            endTime: endTime,
            shouldLimitResults: !showAll,
        }))
    }

    return {
        metrics: singleMetricRequests,
        metoroQLQueries: qlQueries,
        formulas: formulas?.map(f => ({
            ...f,
            formula: f.formula.trim() // Ensure we're not sending any whitespace
        })) || [],
        environments: environments || []
    };
}
